import { memo } from "react";
import { Box, Button, Chip, Container, Stack, Typography } from "@mui/material";
import CheckRounded from "@mui/icons-material/CheckRounded";
import WorkspacePremiumOutlined from "@mui/icons-material/WorkspacePremiumOutlined";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { FullPaddingWrapper } from "../../Components/FullPaddingWrapper/FullPaddingWrapper";
import MessageUsPanel from "../../Components/MessageForm/MessageUsPanel";
import { PREMIUM_PRICE_LABEL } from "../../Components/PremiumGate/PremiumGate";
import { useEntitlements } from "../../Hooks/useEntitlements";
import { useSiteConfig } from "../../Hooks/useSiteConfig";
import { usePlan, formatPlanDate } from "../../Hooks/usePlan";
import { setToggleAuthModal } from "../../Redux/authReducer";
import {
  usePostBillingCheckoutMutation,
  usePostBillingPortalMutation,
} from "../../Services/api.user.slice";

/**
 * /premium. Every "Upgrade to Premium" button lands here. It shows what Premium
 * adds, the price and terms, and — once checkout is open (BILLING_ENABLED) —
 * Get Premium (Stripe Checkout) for free owners, or "You're Premium" + Manage
 * billing for members. Before launch it falls back to a "tell me when it opens"
 * panel so the page is never a dead end.
 */
const FREE = [
  "Claim your home and lock it to you",
  "A preview of your home's permits and records",
  "Ask our team to connect you with a pro",
  "Every section of your home's page",
  "One home photo upload",
];

const PREMIUM = [
  "Everything in Free",
  "Unlimited repairs, improvements and documents in your Home History",
  "Maintenance reminders built for your home",
  "Unlimited home photos",
  "Your complete record, ready to hand over when you sell",
];

const TERMS = "$50 per year · renews automatically until you cancel · no refunds";
const SOURCE_LABEL: Record<string, string> = {
  escrow: "a free year with your home",
  gift: "a gift from SimplerHome",
  launch: "our launch gift",
};

const PlanCard = ({ name, price, items, highlight }: { name: string; price: string; items: string[]; highlight?: boolean }) => (
  <Stack
    spacing={2}
    sx={{
      p: { xs: 3, md: 4 },
      borderRadius: "16px",
      border: highlight ? "2px solid #004B4C" : "1px solid #e3e3e3",
      backgroundColor: highlight ? "#f4f9f9" : "#fff",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      {highlight && <WorkspacePremiumOutlined sx={{ color: "primary.main" }} />}
      <Typography variant="h6" fontWeight={700} color="primary.main">
        {name}
      </Typography>
    </Stack>
    <Typography variant="h4" fontWeight={700}>
      {price}
    </Typography>
    <Stack component="ul" spacing={1.25} sx={{ listStyle: "none", p: 0, m: 0 }}>
      {items.map((item) => (
        <Stack component="li" key={item} direction="row" spacing={1} alignItems="flex-start">
          <CheckRounded sx={{ color: "primary.main", fontSize: 20, mt: "2px" }} />
          <Typography variant="body1">{item}</Typography>
        </Stack>
      ))}
    </Stack>
  </Stack>
);

const Premium = memo(() => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { gatingEnabled } = useEntitlements();
  const { config } = useSiteConfig();
  const { isAuthenticated, plan } = usePlan();
  const [checkout, { isLoading: checkingOut }] = usePostBillingCheckoutMutation();
  const [portal, { isLoading: openingPortal }] = usePostBillingPortalMutation();

  const billingOpen = config.billingEnabled;
  const isPremium = plan?.tier === "premium";
  const isPaid = isPremium && plan?.source === "paid";
  const isGrant = isPremium && !isPaid; // escrow / gift / launch

  const startCheckout = async () => {
    if (!isAuthenticated) {
      dispatch(setToggleAuthModal(true)); // sign in, then come back and press again
      return;
    }
    try {
      const res: any = await checkout().unwrap();
      if (res?.url) window.location.href = res.url;
      else toast.error("Couldn't start checkout. Please try again.");
    } catch (e: any) {
      const code = e?.data?.errors?.errorCode;
      if (code === "ALREADY_PREMIUM") toast("You're already a Premium member.");
      else if (code === "VENDOR_NOT_ELIGIBLE") toast.error("Vendor accounts don't use homeowner Premium.");
      else toast.error("Couldn't start checkout. Please try again.");
    }
  };

  const openBillingPortal = async () => {
    try {
      const res: any = await portal().unwrap();
      if (res?.url) window.location.href = res.url;
      else toast.error("Couldn't open billing. Please try again.");
    } catch {
      toast.error("Couldn't open billing. Please try again.");
    }
  };

  const until = formatPlanDate(plan?.until);

  const renderCta = () => {
    // Before launch: never a dead end — keep the "tell me when it opens" panel.
    if (!billingOpen) {
      return (
        <>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Premium sign-up opens soon. {gatingEnabled ? "" : "Until then every feature is free. "}Leave your details and
            we'll tell you the day it opens, or ask us anything about it.
          </Typography>
          <MessageUsPanel
            align="center"
            topic="premium"
            title="Tell me when Premium opens"
            defaultMessage="Please let me know when Premium opens."
            submitLabel="Keep me posted"
            data-testid="premium-interest"
          />
          <Button variant="text" onClick={() => navigate("/")} sx={{ textTransform: "none" }}>
            Find and claim your home
          </Button>
        </>
      );
    }

    // Paying member — manage the subscription.
    if (isPaid) {
      return (
        <Stack spacing={1.5} alignItems="center" data-testid="premium-member-paid">
          <Chip color="success" icon={<CheckRounded />} label="You're Premium" sx={{ fontWeight: 700, py: 2, px: 1 }} />
          <Typography variant="body1" color="text.secondary" textAlign="center">
            {plan?.cancelAtPeriodEnd
              ? `Your Premium ends on ${until}. You can renew anytime.`
              : until
              ? `Renews automatically on ${until}.`
              : "Your Premium is active."}
          </Typography>
          <Button
            variant="contained"
            onClick={openBillingPortal}
            disabled={openingPortal}
            sx={{ textTransform: "none", borderRadius: 2, px: 4, fontWeight: 700 }}
            data-testid="premium-manage-billing"
          >
            {openingPortal ? "Opening…" : "Manage billing"}
          </Button>
          <Button variant="text" onClick={() => navigate("/")} sx={{ textTransform: "none" }}>
            Go to my home
          </Button>
        </Stack>
      );
    }

    // Comp grant (escrow / gift / launch) — offer to subscribe to keep it.
    if (isGrant) {
      const src = plan?.source ? SOURCE_LABEL[plan.source] || "Premium" : "Premium";
      return (
        <Stack spacing={1.5} alignItems="center" data-testid="premium-member-grant">
          <Chip color="success" icon={<CheckRounded />} label="You're Premium" sx={{ fontWeight: 700, py: 2, px: 1 }} />
          <Typography variant="body1" color="text.secondary" textAlign="center">
            You have Premium — {src}{until ? `, through ${until}` : ""}.
            {until ? " Subscribe now to keep it after that." : ""}
          </Typography>
          {until && (
            <>
              <Button
                variant="contained"
                onClick={startCheckout}
                disabled={checkingOut}
                startIcon={<WorkspacePremiumOutlined />}
                sx={{ textTransform: "none", borderRadius: 2, px: 4, fontWeight: 700 }}
                data-testid="premium-subscribe-keep"
              >
                {checkingOut ? "Starting…" : "Subscribe to keep Premium"}
              </Button>
              <Typography variant="caption" color="text.secondary">{TERMS}</Typography>
            </>
          )}
          <Button variant="text" onClick={() => navigate("/")} sx={{ textTransform: "none" }}>
            Go to my home
          </Button>
        </Stack>
      );
    }

    // Free (or signed out) — Get Premium.
    return (
      <Stack spacing={1.5} alignItems="center" data-testid="premium-get">
        <Button
          variant="contained"
          onClick={startCheckout}
          disabled={checkingOut}
          startIcon={<WorkspacePremiumOutlined />}
          sx={{ textTransform: "none", borderRadius: 2, px: 5, py: 1.25, fontWeight: 700, fontSize: "1.05rem" }}
          data-testid="premium-get-cta"
        >
          {checkingOut ? "Starting…" : "Get Premium"}
        </Button>
        <Typography variant="body2" color="text.secondary" textAlign="center">{TERMS}</Typography>
        {!isAuthenticated && (
          <Typography variant="caption" color="text.secondary">You'll sign in first.</Typography>
        )}
      </Stack>
    );
  };

  return (
    <Container maxWidth="xl" disableGutters>
      <FullPaddingWrapper>
        <Box sx={{ pt: { xs: 3, md: 5 }, pb: { xs: 3, md: 5 }, textAlign: { md: "center" } }}>
          <Typography variant="h3" component="h1" fontWeight={700} color="primary.main" sx={{ fontSize: { xs: "1.9rem", md: "2.75rem" }, mb: 1.5 }}>
            SimplerHome Premium
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640, mx: { md: "auto" }, fontSize: { md: "1.1rem" } }}>
            Your home's whole story, kept up to date, for {PREMIUM_PRICE_LABEL.replace("/", " a ")}.
          </Typography>
          {!gatingEnabled && (
            <Chip
              color="success"
              variant="outlined"
              label="Free for everyone during our Los Angeles launch"
              sx={{ mt: 2.5, fontWeight: 600, height: "auto", py: 0.75, "& .MuiChip-label": { whiteSpace: "normal" } }}
              data-testid="premium-launch-free"
            />
          )}
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: { xs: 2, md: 3 }, maxWidth: 960, mx: "auto", mb: { xs: 5, md: 8 } }}>
          <PlanCard name="Free" price="$0" items={FREE} />
          <PlanCard name="Premium" price={PREMIUM_PRICE_LABEL} items={PREMIUM} highlight />
        </Box>

        <Stack spacing={3} alignItems="center" sx={{ maxWidth: 680, mx: "auto" }}>
          {renderCta()}
        </Stack>
      </FullPaddingWrapper>
    </Container>
  );
});

export default Premium;
